/**
 * @fileoverview Loading, validation and geometry helpers for `data/patterns.json`.
 *
 * Coordinates are inches, origin top-left, y increasing downward. Tile ids are
 * stable across data releases and are the key for per-tile colors.
 *
 * Kept free of Vue/Pinia so it can be unit tested directly.
 *
 * @module patternData
 */

/** Grout joint width in inches (3/16"). */
export const GROUT_WIDTH_IN = 0.1875;

/** Fill and outline for a tile that has not been painted yet. */
export const UNPAINTED_FILL = '#ffffff';
export const UNPAINTED_STROKE = '#c9c3b8';

/**
 * Formats a nominal size key for display, e.g. `4x12` → `4×12`.
 * @param {string} size
 * @returns {string}
 */
export function formatSize(size) {
    return String(size || '').replace('x', '×');
}

/**
 * Validates one pattern against the checks in the data brief.
 * @param {Object} p - Pattern entry
 * @returns {string[]} Error messages (empty when valid)
 */
export function validatePattern(p) {
    const errors = [];
    const id = p?.id || '(missing id)';
    const width = p?.width;
    const height = p?.height;
    const tiles = Array.isArray(p?.tiles) ? p.tiles : [];

    if (!p?.id) errors.push(`${id}: missing id`);
    if (!Number.isInteger(width) || !Number.isInteger(height) || width <= 0 || height <= 0) {
        errors.push(`${id}: invalid sheet size`);
        return errors;
    }
    if (!Array.isArray(p.sizes) || p.sizes.length < 2) {
        errors.push(`${id}: needs at least two sizes`);
    }

    const grid = Array.from({ length: height }, () => new Uint8Array(width));
    const tileIds = new Set();

    for (const t of tiles) {
        if (tileIds.has(t.id)) errors.push(`${id}: duplicate tile id ${t.id}`);
        tileIds.add(t.id);

        if (t.x < 0 || t.y < 0 || t.x + t.w > width || t.y + t.h > height) {
            errors.push(`${id}: ${t.id} out of bounds`);
            continue;
        }
        const expectedSize = `${Math.min(t.w, t.h)}x${Math.max(t.w, t.h)}`;
        if (t.size !== expectedSize) {
            errors.push(`${id}: ${t.id} size ${t.size} does not match ${t.w}x${t.h}`);
        }
        for (let y = t.y; y < t.y + t.h; y++) {
            for (let x = t.x; x < t.x + t.w; x++) {
                if (grid[y][x]) {
                    errors.push(`${id}: overlap at ${x},${y}`);
                    return errors;
                }
                grid[y][x] = 1;
            }
        }
    }
    if (grid.some((row) => row.some((c) => !c))) errors.push(`${id}: gap`);

    const countSum = Object.values(p.tileCounts || {}).reduce((a, b) => a + b, 0);
    if (countSum !== p.tileCount || p.tileCount !== tiles.length) {
        errors.push(`${id}: tileCounts/tileCount/tiles length mismatch`);
    }
    return errors;
}

/**
 * Validates the whole data file. Invalid patterns are dropped and reported so
 * one bad entry cannot take down the tool.
 * @param {Object} data - Parsed patterns.json
 * @returns {{ patterns: Object[], errors: string[] }}
 */
export function loadPatterns(data) {
    const errors = [];
    const patterns = [];
    const seen = new Set();
    for (const p of data?.patterns || []) {
        const pErrors = validatePattern(p);
        if (seen.has(p.id)) pErrors.push(`${p.id}: duplicate pattern id`);
        seen.add(p.id);
        if (pErrors.length) {
            errors.push(...pErrors);
            continue;
        }
        patterns.push(p);
    }
    patterns.sort((a, b) => a.index - b.index);
    return { patterns, errors };
}

/**
 * Tile ids sorted ascending; this is the canonical order for share-link encoding.
 * @param {Object} pattern
 * @returns {string[]}
 */
export function sortedTileIds(pattern) {
    return (pattern?.tiles || []).map((t) => t.id).sort();
}

/**
 * Finds the tile under a point in sheet coordinates (inches). Points in a
 * repeated field are wrapped back onto the base sheet, so a click on any
 * copy resolves to the base tile.
 * @param {Object} pattern
 * @param {number} x
 * @param {number} y
 * @param {number} [repeat=1] - Copies per side in the field view
 * @returns {Object|null} The tile, or null when outside the field
 */
export function hitTestTile(pattern, x, y, repeat = 1) {
    if (!pattern) return null;
    const { width, height } = pattern;
    if (x < 0 || y < 0 || x >= width * repeat || y >= height * repeat) return null;
    const sx = x % width;
    const sy = y % height;
    for (const t of pattern.tiles) {
        if (sx >= t.x && sx < t.x + t.w && sy >= t.y && sy < t.y + t.h) return t;
    }
    return null;
}
